import {
	motion,
	useInView,
	useReducedMotion,
	useScroll,
	useTransform,
	type Variants,
} from "motion/react";
import type React from "react";
import { useEffect, useRef, useState } from "react";

/** One motion vocabulary for the whole site. */
export const EASE = [0.22, 1, 0.36, 1] as const;
const DURATION = {
	state: 0.18,
	swap: 0.45,
	reveal: 0.8,
	draw: 1.6,
} as const;
const VIEWPORT = { once: true, margin: "0px 0px -12% 0px" } as const;

type RevealProps = {
	children: React.ReactNode;
	className?: string;
	delay?: number;
	y?: number;
};

/** Fade and rise once when the block enters the viewport. */
export function Reveal({
	children,
	className,
	delay = 0,
	y = 16,
}: RevealProps) {
	return (
		<motion.div
			className={className}
			initial={{ opacity: 0, y }}
			whileInView={{ opacity: 1, y: 0 }}
			viewport={VIEWPORT}
			transition={{ duration: DURATION.reveal, ease: EASE, delay }}
		>
			{children}
		</motion.div>
	);
}

const groupVariants: Variants = {
	hidden: {},
	visible: { transition: { staggerChildren: 0.09 } },
};

const itemVariants: Variants = {
	hidden: { opacity: 0, y: 16 },
	visible: {
		opacity: 1,
		y: 0,
		transition: { duration: DURATION.reveal, ease: EASE },
	},
};

/** Staggered reveal: wrap items in `RevealItem`. */
export function RevealGroup({
	children,
	className,
	as = "div",
}: {
	children: React.ReactNode;
	className?: string;
	as?: "div" | "ul" | "ol" | "dl";
}) {
	const Component = motion[as];
	return (
		<Component
			className={className}
			variants={groupVariants}
			initial="hidden"
			whileInView="visible"
			viewport={VIEWPORT}
		>
			{children}
		</Component>
	);
}

/**
 * Paper fed out of the printer: the sheet slides down out of the slot while it is
 * printed top to bottom. Same staggering as `RevealItem`.
 */
const feedVariants: Variants = {
	hidden: { clipPath: "inset(0 0 100% 0)", y: -14 },
	visible: {
		clipPath: "inset(0 0 -2% 0)",
		y: 0,
		transition: { duration: 1.05, ease: EASE },
	},
};

export function RevealItem({
	children,
	className,
	as = "div",
	feed = false,
}: {
	children: React.ReactNode;
	className?: string;
	as?: "div" | "li" | "article";
	/** Print the item out of the slot instead of fading it in. */
	feed?: boolean;
}) {
	const Component = motion[as];
	return (
		<Component
			className={className}
			variants={feed ? feedVariants : itemVariants}
		>
			{children}
		</Component>
	);
}

/** Easing that advances in `n` hard steps: typewriter, dot-matrix head. */
export const steps =
	(n: number) =>
	(progress: number): number =>
		Math.min(1, Math.floor(progress * n) / n);

/**
 * Words rising out of their line mask on first paint. CSS keyframes, so it plays
 * before hydration (use it for the LCP heading) and settles under reduced motion.
 */
export function RiseWords({
	text,
	delay = 0,
	step = 0.055,
	from = 0,
}: {
	text: string;
	/** Seconds before the first word. */
	delay?: number;
	/** Seconds between two words. */
	step?: number;
	/** Index offset, to chain several `RiseWords` in one line. */
	from?: number;
}) {
	const words = text.split(" ").filter(Boolean);
	return (
		<>
			{words.map((word, i) => (
				<span key={`${word}-${i}`}>
					<span className="rise-mask">
						<span
							className="rise-word"
							style={
								{
									"--i": from + i,
									"--d": `${delay}s`,
									"--step": `${step}s`,
								} as React.CSSProperties
							}
						>
							{word}
						</span>
					</span>
					{i < words.length - 1 ? " " : null}
				</span>
			))}
		</>
	);
}

/** Words rising one after the other when the paragraph scrolls into view. */
export function WordsInView({
	text,
	className,
}: {
	text: string;
	className?: string;
}) {
	const words = text.split(" ").filter(Boolean);
	return (
		<motion.p
			className={className}
			initial="hidden"
			whileInView="visible"
			viewport={VIEWPORT}
			variants={{ visible: { transition: { staggerChildren: 0.035 } } }}
		>
			{words.map((word, i) => (
				<span key={`${word}-${i}`}>
					<span className="rise-mask">
						<motion.span
							className="inline-block"
							variants={{
								hidden: { y: "125%" },
								visible: {
									y: 0,
									transition: { duration: DURATION.reveal, ease: EASE },
								},
							}}
						>
							{word}
						</motion.span>
					</span>
					{i < words.length - 1 ? " " : null}
				</span>
			))}
		</motion.p>
	);
}

/** `pastel-mark` whose highlighter is dragged under the phrase when it comes into view. */
export function MarkDraw({
	children,
	className,
	delay = 0.25,
}: {
	children: React.ReactNode;
	className?: string;
	delay?: number;
}) {
	return (
		<motion.span
			className={className ? `pastel-mark ${className}` : "pastel-mark"}
			initial={{ backgroundSize: "0% 50%" }}
			whileInView={{ backgroundSize: "100% 50%" }}
			viewport={VIEWPORT}
			transition={{ duration: 0.9, ease: EASE, delay }}
		>
			{children}
		</motion.span>
	);
}

/**
 * Cycles through scene states while the scene is on screen and the tab is visible.
 * Server render and reduced motion stay on `initial`, the scene's most telling state.
 */
export function useSceneLoop(
	durations: readonly number[],
	{ initial = 0 }: { initial?: number } = {},
) {
	const ref = useRef<HTMLDivElement>(null);
	const inView = useInView(ref, { margin: "-10% 0px -10% 0px" });
	const reducedMotion = useReducedMotion();
	const [index, setIndex] = useState(initial);
	const [pageVisible, setPageVisible] = useState(true);

	useEffect(() => {
		const onVisibility = () =>
			setPageVisible(document.visibilityState === "visible");
		document.addEventListener("visibilitychange", onVisibility);
		return () => document.removeEventListener("visibilitychange", onVisibility);
	}, []);

	const playing = inView && pageVisible && !reducedMotion;

	useEffect(() => {
		if (!playing) return;
		const timer = window.setTimeout(
			() => setIndex((current) => (current + 1) % durations.length),
			durations[index],
		);
		return () => window.clearTimeout(timer);
	}, [playing, index, durations]);

	return { ref, index };
}

/**
 * Scroll-linked plate for a large product scene: the frame opens from a tighter
 * crop while the scene settles from a slight zoom. Reduced motion pins both.
 */
export function ScrollPlate({
	children,
	chrome,
	className,
}: {
	children: React.ReactNode;
	/** Frame furniture (overlay, "Example" tag) that stays still above the scene. */
	chrome?: React.ReactNode;
	className?: string;
}) {
	const ref = useRef<HTMLDivElement>(null);
	const { scrollYProgress } = useScroll({
		target: ref,
		offset: ["start end", "center 60%"],
	});
	const inset = useTransform(scrollYProgress, [0, 1], [9, 0]);
	const clipPath = useTransform(
		inset,
		(v) => `inset(${v}% ${v * 0.7}% round 0)`,
	);
	const scale = useTransform(scrollYProgress, [0, 1], [1.14, 1]);
	return (
		<motion.div
			ref={ref}
			className={`${className ?? ""} motion-reduce:[clip-path:none!important]`}
			style={{ clipPath }}
		>
			<motion.div
				className="h-full w-full motion-reduce:[transform:none!important]"
				style={{ scale }}
			>
				{children}
			</motion.div>
			{chrome}
		</motion.div>
	);
}
